import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@/core/api/types';
import {
  isSupportedComposerImage,
  MAX_COMPOSER_IMAGES,
  type ComposerUploadMetrics,
  type ComposerUploadStage,
  uploadDetachedComposerImage,
} from '../services/composerImageUpload';

export interface ComposerImageItem {
  id: string;
  file: File;
  previewUrl: string;
  status: ComposerUploadStage;
  progress: number;
  remoteUrl?: string;
  error?: string | null;
  metrics?: ComposerUploadMetrics;
}

interface WaitForUploadsResult {
  imageUrls: string[];
  uploadMetrics?: Record<string, number>;
}

export function useComposerImages() {
  const [items, setItems] = useState<ComposerImageItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const tasksRef = useRef(new Map<string, Promise<void>>());
  const itemsRef = useRef<ComposerImageItem[]>([]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    return () => {
      for (const item of itemsRef.current) {
        URL.revokeObjectURL(item.previewUrl);
      }
    };
  }, []);

  const remaining = Math.max(0, MAX_COMPOSER_IMAGES - items.length);
  const hasPendingUploads = items.some((item) => item.status !== 'uploaded');
  const aggregateUploadProgress =
    items.length === 0
      ? 1
      : items.reduce((sum, item) => sum + effectiveProgress(item), 0) / items.length;

  async function addFiles(files: File[]): Promise<void> {
    if (files.length === 0) return;

    const supported = files.filter(isSupportedComposerImage);
    const limited = supported.slice(0, remaining);
    const nextError = buildAddFilesError({
      total: files.length,
      supported: supported.length,
      accepted: limited.length,
      remaining,
    });
    setError(nextError);

    const nextItems = limited.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'pending' as const,
      progress: 0,
      error: null,
    }));

    if (nextItems.length === 0) return;
    setItems((current) => [...current, ...nextItems]);
    for (const item of nextItems) {
      startUpload(item.id, item.file);
    }
  }

  function remove(id: string): void {
    const existing = itemsRef.current.find((item) => item.id === id);
    if (existing) URL.revokeObjectURL(existing.previewUrl);
    tasksRef.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
    setError(null);
  }

  function clearAll(): void {
    for (const item of itemsRef.current) {
      URL.revokeObjectURL(item.previewUrl);
    }
    tasksRef.current.clear();
    setItems([]);
    setError(null);
  }

  async function waitForAllUploads(): Promise<WaitForUploadsResult> {
    retryUnfinishedUploads();
    while (tasksRef.current.size > 0) {
      await Promise.all(Array.from(tasksRef.current.values()));
    }

    const latestItems = itemsRef.current;
    const failed = latestItems.find((item) => item.status !== 'uploaded');
    if (failed) {
      throw new Error(failed.error || '图片上传失败，请重试');
    }

    return {
      imageUrls: latestItems.map((item) => item.remoteUrl).filter(isNonEmptyString),
      uploadMetrics: buildImageClientMetrics(latestItems),
    };
  }

  function retryUnfinishedUploads(): void {
    for (const item of itemsRef.current) {
      if (item.status !== 'uploaded') {
        startUpload(item.id, item.file, item.status === 'failed');
      }
    }
  }

  function startUpload(id: string, file: File, forceRestart = false): void {
    if (tasksRef.current.has(id) && !forceRestart) return;
    setItems((current) =>
      current.map((item) =>
        item.id === id
          ? {
              ...item,
              status: 'requesting_upload_url',
              progress: Math.max(item.progress, 0.01),
              error: null,
            }
          : item
      )
    );

    const task = uploadDetachedComposerImage(file, (snapshot) => {
      setItems((current) =>
        current.map((item) =>
          item.id === id
            ? {
                ...item,
                status: snapshot.stage,
                progress: snapshot.overallProgress,
              }
            : item
        )
      );
    })
      .then((result) => {
        setItems((current) =>
          current.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: 'uploaded',
                  progress: 1,
                  remoteUrl: result.fileUrl,
                  metrics: result.metrics,
                  error: null,
                }
              : item
          )
        );
      })
      .catch((err: unknown) => {
        const message = err instanceof ApiError ? err.message : (err as Error).message || '上传失败';
        setItems((current) =>
          current.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: 'failed',
                  error: message,
                }
              : item
          )
        );
      })
      .finally(() => {
        tasksRef.current.delete(id);
      });

    tasksRef.current.set(id, task);
  }

  return {
    items,
    error,
    remaining,
    hasPendingUploads,
    aggregateUploadProgress,
    addFiles,
    remove,
    clearAll,
    waitForAllUploads,
  };
}

function effectiveProgress(item: ComposerImageItem): number {
  return item.status === 'uploaded' ? 1 : Math.max(0, Math.min(1, item.progress));
}

function buildAddFilesError(input: {
  total: number;
  supported: number;
  accepted: number;
  remaining: number;
}): string | null {
  if (input.remaining <= 0) return `最多上传 ${MAX_COMPOSER_IMAGES} 张图片`;
  if (input.supported === 0 && input.total > 0) return '仅支持 PNG、JPG、GIF、WebP 图片';
  if (input.supported < input.total) return '部分文件已跳过，仅支持 PNG、JPG、GIF、WebP 图片';
  if (input.accepted < input.supported) return `最多上传 ${MAX_COMPOSER_IMAGES} 张图片`;
  return null;
}

function buildImageClientMetrics(
  items: ComposerImageItem[]
): Record<string, number> | undefined {
  const metricsItems = items.map((item) => item.metrics).filter(Boolean) as ComposerUploadMetrics[];
  if (metricsItems.length === 0) return undefined;

  return {
    client_pre_request_ms: 0,
    upload_total_ms: metricsItems.reduce((sum, metric) => sum + metric.totalMs, 0),
    upload_get_url_ms: metricsItems.reduce((sum, metric) => sum + metric.getUploadUrlMs, 0),
    upload_oss_ms: metricsItems.reduce((sum, metric) => sum + metric.uploadOssMs, 0),
    upload_confirm_ms: metricsItems.reduce((sum, metric) => sum + metric.confirmUploadMs, 0),
    upload_file_count: metricsItems.length,
  };
}

function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}
